import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Formik, Form } from "formik";
import logo from "../assets/logo.svg";
import seasonConfig from "../data/season.config.json";
import {
  Sun, Moon, AlertCircle, ChevronLeft, ChevronRight, Send,
  CheckCircle2, User, Building2, CreditCard, ClipboardCheck, Tag,
} from "lucide-react";
import Confetti from "react-confetti";

import { useTheme } from "../Providers/ThemeContext";
import { getValidationSchema } from "../services/validationSchema";
import { Step1 } from "../Step/PersonalInfo";
import { Step2 } from "../Step/AssociationInfo";
import { Step3 } from "../Step/MembershipFee";
import { Step4 } from "../Step/Confirmation";
import CookieBanner from "../components/CookiesBanners";
import { useParams } from "react-router";
import { submitForm } from "../services/services";
import Confirmed from "../Step/Confirmed";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";

/* ── Confettis via portal ── */
function ConfettiPortal() {
  const [active, setActive] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setActive(false), 7000);
    return () => clearTimeout(t);
  }, []);

  if (!active) return null;

  return ReactDOM.createPortal(
    <Confetti
      width={window.innerWidth}
      height={window.innerHeight}
      numberOfPieces={350}
      recycle={false}
      colors={["#609BAB", "#E87E58", "#ffffff", "#ffd700", "#a78bfa", "#34d399"]}
      style={{ position: "fixed", top: 0, left: 0, zIndex: 9999, pointerEvents: "none" }}
    />,
    document.body
  );
}

const STEPS_CONFIG = [
  { id: 1, icon: User,           label: "Informations", desc: "Identité & coordonnées"   },
  { id: 2, icon: Building2,      label: "Association",  desc: "Discord & domaines"        },
  { id: 3, icon: CreditCard,     label: "Cotisation",   desc: "Statut & paiement"         },
  { id: 4, icon: ClipboardCheck, label: "Confirmation", desc: "Récapitulatif & signature" },
];

/* ══════════════════════════════════════════════════════
   FORM MANAGER
══════════════════════════════════════════════════════ */
const FormManager = () => {
  const { token } = useParams();
  const [isInvited, setIsInvited]     = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [confirmed, setConfirmed]     = useState(false);
  const { theme, toggleTheme }        = useTheme();

  const allSteps      = [1, 2, 3, 4];
  const filteredSteps = isInvited ? allSteps.filter((s) => s !== 3) : allSteps;
  const visibleConfig = STEPS_CONFIG.filter((s) => !isInvited || s.id !== 3);
  const totalSteps    = 4;

  const currentIndex = filteredSteps.indexOf(currentStep);
  const progress     = ((currentIndex + 1) / filteredSteps.length) * 100;

  const nextStep = () => { const n = filteredSteps[currentIndex + 1]; if (n) setCurrentStep(n); };
  const prevStep = () => { const p = filteredSteps[currentIndex - 1]; if (p !== undefined) setCurrentStep(p); };
  const goToStep = (s)  => { if (filteredSteps.includes(s)) setCurrentStep(s); };

  const handleSubmit = async (values) => {
    try {
      await submitForm(values, isInvited);
      setCurrentStep(5);
      setConfirmed(true);
    } catch (err) {
      console.error("Failed to submit form:", err);
    }
  };

  useEffect(() => {
    if (token === "bWVtYnJlY29udmllcg==") setIsInvited(true);
  }, [token]);

  /* ─── Sidebar step button ─── */
  const SidebarStep = ({ step, idx }) => {
    const realIdx   = filteredSteps.indexOf(step.id);
    const isDone    = realIdx < currentIndex;
    const isCurrent = step.id === currentStep;
    const Icon      = step.icon;

    return (
      <button
        type="button"
        onClick={() => goToStep(step.id)}
        className={cn(
          "group flex items-start gap-3 px-3 py-2.5 rounded-xl text-left w-full transition-all duration-200",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          isCurrent  ? "bg-primary/15 border border-primary/30 shadow-sm"
                     : "hover:bg-muted/50 border border-transparent",
        )}
      >
        {/* Cercle */}
        <div className={cn(
          "w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs font-bold mt-0.5",
          "transition-all duration-200",
          isDone     && "bg-primary text-primary-foreground",
          isCurrent  && "bg-primary text-primary-foreground ring-2 ring-primary/30 ring-offset-1 ring-offset-card",
          !isDone && !isCurrent && "bg-muted border border-border text-muted-foreground group-hover:border-primary/40",
        )}>
          {isDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
        </div>

        {/* Labels */}
        <div className="min-w-0 leading-tight">
          <p className={cn(
            "text-xs font-semibold",
            isCurrent ? "text-primary" : isDone ? "text-foreground" : "text-muted-foreground",
          )}>
            {step.label}
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
            {step.desc}
          </p>
        </div>
      </button>
    );
  };

  /* ─── Mobile step dot ─── */
  const MobileDot = ({ step, idx }) => {
    const realIdx   = filteredSteps.indexOf(step.id);
    const isDone    = realIdx < currentIndex;
    const isCurrent = step.id === currentStep;
    const Icon      = step.icon;

    return (
      <button
        type="button"
        onClick={() => goToStep(step.id)}
        className="flex flex-col items-center gap-1 focus:outline-none"
        aria-label={step.label}
      >
        <div className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200",
          isDone     && "bg-primary text-primary-foreground",
          isCurrent  && "bg-primary text-primary-foreground ring-2 ring-primary/40 ring-offset-1 ring-offset-card",
          !isDone && !isCurrent && "bg-muted border border-border text-muted-foreground",
        )}>
          {isDone
            ? <CheckCircle2 className="h-3.5 w-3.5" />
            : <Icon className="h-3.5 w-3.5" />
          }
        </div>
        <span className={cn(
          "text-[9px] font-semibold",
          isCurrent ? "text-primary" : isDone ? "text-foreground/70" : "text-muted-foreground",
        )}>
          {step.label}
        </span>
      </button>
    );
  };

  /* ══ RENDER ══ */
  return (
    <>
      {confirmed && <ConfettiPortal />}
      <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-5">
      <div className={cn(
        "flex flex-col w-full max-w-4xl max-h-[85vh]",
        "rounded-2xl overflow-hidden",
        "bg-card text-card-foreground",
        "shadow-[0_32px_80px_rgba(0,0,0,0.60),0_8px_24px_rgba(0,0,0,0.30),inset_0_1px_0_rgba(255,255,255,0.07)]",
        "border border-white/10",
      )}>

        {/* ══ HEADER ══ */}
        <header className={cn(
          "shrink-0 flex items-center justify-between gap-3 px-4 sm:px-5 py-3",
          "border-b border-border/40",
          "bg-gradient-to-r from-primary/12 via-primary/4 to-transparent",
        )}>
          {/* Branding */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative shrink-0">
              <img src={logo} alt="ImagiGeeks" className="w-8 h-8" />
              <div className="absolute -inset-1 rounded-full bg-primary/20 blur-sm -z-10" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-sm font-bold text-foreground leading-tight">
                  {isInvited ? "Formulaire convié" : "Formulaire d'adhésion"}
                </h1>
                {isInvited && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-secondary/20 text-secondary border border-secondary/30">
                    <Tag className="h-2.5 w-2.5" />
                    Convié
                  </span>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">
                ImagiGeeks · <span className="text-primary font-medium">{seasonConfig.currentSeason}</span>
              </p>
            </div>
          </div>

          {/* Theme toggle */}
          <Button
            type="button"
            onClick={toggleTheme}
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground shrink-0"
            aria-label="Changer le thème"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </header>

        {/* ══ MOBILE STEPPER ══ */}
        {!confirmed && (
          <div className="md:hidden shrink-0 px-4 pt-3 pb-2 border-b border-border/30 bg-muted/10">
            <div className="flex items-start gap-1">
              {visibleConfig.map((step, idx) => {
                const realIdx = filteredSteps.indexOf(step.id);
                return (
                  <React.Fragment key={step.id}>
                    <MobileDot step={step} idx={idx} />
                    {idx < visibleConfig.length - 1 && (
                      <div className={cn(
                        "flex-1 h-px mt-4 transition-colors duration-500",
                        filteredSteps.indexOf(step.id) < currentIndex ? "bg-primary" : "bg-border/60",
                      )} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {/* Progress bar */}
            <div className="mt-2.5 h-1 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* ══ BODY ══ */}
        <div className="flex flex-1 min-h-0 overflow-hidden">

          {/* ── Sidebar desktop ── */}
          {!confirmed && (
            <aside className="hidden md:flex w-52 shrink-0 flex-col border-r border-border/40 bg-gradient-to-b from-primary/6 to-transparent">
              <nav className="flex-1 px-3 py-4 flex flex-col gap-0.5">
                {visibleConfig.map((step, idx) => (
                  <SidebarStep key={step.id} step={step} idx={idx} />
                ))}
              </nav>

              {/* Séparateur + progression */}
              <div className="px-4 pb-5">
                <div className="h-px bg-border/50 mb-4" />
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1.5">
                  <span className="font-medium">Progression</span>
                  <span className="text-primary font-bold">{Math.round(progress)}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-[9px] text-muted-foreground/70 mt-2 text-center">
                  Étape {currentIndex + 1} / {filteredSteps.length}
                </p>
              </div>
            </aside>
          )}

          {/* ── Zone formulaire ── */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            <Formik
              initialValues={{
                nom: "", prenom: "", pseudo: "", dateNaissance: "",
                adressePostale: "", zipCode: "", city: "",
                email: "", telephone: "",
                allergies: "", notes: "", pmr: false,
                idDiscord: "", domainePratique: [], motivation: "",
                cosplay: "", cultureGeek: "", cultureImaginaire: "",
                statut: "", modePaiement: "",
                reglementInterieurLu: false, dateSignature: "",
                vehicule: false,
              }}
              validationSchema={getValidationSchema(isInvited)}
              onSubmit={async (values, { setSubmitting }) => {
                await handleSubmit(values);
                setSubmitting(false);
              }}
            >
              {({ errors, isSubmitting, values }) => (
                <Form className="flex flex-col flex-1 overflow-hidden min-h-0">

                  {/* ── Contenu step ── */}
                  <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                    <div className="px-4 sm:px-6 py-4 step-enter">
                      {currentStep === 1 && <Step1 />}
                      {currentStep === 2 && <Step2 />}
                      {currentStep === 3 && !isInvited && <Step3 />}
                      {currentStep === 4 && <Step4 values={values} isInvited={isInvited} />}
                      {confirmed && <Confirmed values={values} />}
                    </div>
                  </div>

                  {/* ── Footer navigation ── */}
                  {!confirmed && (
                    <footer className={cn(
                      "shrink-0 flex items-center justify-between gap-3 px-4 sm:px-5 py-3",
                      "border-t border-border/30 bg-muted/8",
                    )}>
                      {/* Indicateur champs requis */}
                      <span className="text-[10px] text-muted-foreground hidden sm:flex items-center gap-1">
                        <span className="text-secondary font-bold">*</span>
                        Champs requis
                      </span>

                      {/* Boutons */}
                      <div className="flex items-center gap-2 ml-auto">
                        {currentStep > 1 && (
                          <Button
                            type="button"
                            onClick={prevStep}
                            variant="outline"
                            size="sm"
                            className="gap-1.5 text-xs h-8 px-3"
                          >
                            <ChevronLeft className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Précédent</span>
                          </Button>
                        )}

                        {currentStep < totalSteps ? (
                          <Button
                            type="button"
                            onClick={nextStep}
                            size="sm"
                            className="gap-1.5 text-xs h-8 px-3 bg-primary hover:bg-primary/90"
                          >
                            <span className="hidden sm:inline">Suivant</span>
                            <ChevronRight className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <div className="flex items-center gap-2">
                            {Object.keys(errors).length > 0 && (
                              <p className="text-[10px] text-destructive flex items-center gap-1">
                                <AlertCircle className="h-3 w-3 shrink-0" />
                                <span className="hidden sm:inline">Corrigez les erreurs.</span>
                              </p>
                            )}
                            <Button
                              type="submit"
                              disabled={isSubmitting || Object.keys(errors).length > 0}
                              size="sm"
                              className="gap-1.5 text-xs h-8 px-3 bg-secondary text-secondary-foreground hover:bg-secondary/90"
                            >
                              <Send className="h-3.5 w-3.5" />
                              Soumettre
                            </Button>
                          </div>
                        )}
                      </div>
                    </footer>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      <CookieBanner />
    </div>
    </>
  );
};

export default FormManager;
